import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Search, Edit, Trash2, FileWarning, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../utils/helpers';
import AddClaimDialog, { CLAIM_TYPES, CLAIM_STATUSES } from '../components/AddClaimDialog';
import { usePagedTable, SortableTh, PaginationBar } from '../utils/tableUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPEN_STATUSES = new Set(['Intimated', 'Documents pending', 'Submitted to insurer', 'Under review', 'Query raised']);

const statusTone = (s) => {
  if (s === 'Settled') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (s === 'Rejected') return 'bg-red-100 text-red-800 border-red-200';
  if (s === 'Closed') return 'bg-slate-100 text-slate-700 border-slate-200';
  if (s === 'Query raised') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-blue-100 text-blue-800 border-blue-200';
};

const Claims = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('attention') === '1' ? 'attention' : 'all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/claims`, { withCredentials: true });
      setRows(data || []);
    } catch (e) {
      toast.error('Failed to load claims');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    let out = rows;
    if (statusFilter === 'attention') {
      out = out.filter((r) => OPEN_STATUSES.has(r.status) && r.next_followup_date && r.next_followup_date <= today);
    } else if (statusFilter !== 'all') {
      out = out.filter((r) => r.status === statusFilter);
    }
    if (typeFilter !== 'all') out = out.filter((r) => r.claim_type === typeFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter((r) =>
        (r.claim_number || '').toLowerCase().includes(q) ||
        (r.client_name || '').toLowerCase().includes(q) ||
        (r.policy_number || '').toLowerCase().includes(q) ||
        (r.insurer_claim_ref || '').toLowerCase().includes(q)
      );
    }
    return out;
  }, [rows, statusFilter, typeFilter, query]);

  const openCount = useMemo(
    () => rows.filter((r) => OPEN_STATUSES.has(r.status)).length,
    [rows]
  );
  const attentionCount = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return rows.filter((r) => OPEN_STATUSES.has(r.status) && r.next_followup_date && r.next_followup_date <= today).length;
  }, [rows]);

  const table = usePagedTable({ rows: filteredRows, defaultPageSize: 25, defaultSort: { key: 'created_at', dir: 'desc' } });

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/claims/${deleting.claim_id}`, { withCredentials: true });
      toast.success('Claim deleted');
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="claims-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Claims</h1>
          <p className="text-slate-600 mt-1 text-sm">Track every claim from intimation to settlement.</p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-blue-800 hover:bg-blue-900" data-testid="claims-new-button">
          <Plus className="h-4 w-4 mr-1.5" /> New Claim
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="claims-kpis">
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5">
          <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Total Claims</p>
          <p className="text-[22px] font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>{rows.length}</p>
        </div>
        <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2.5" data-testid="kpi-open-claims">
          <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Open Claims</p>
          <p className="text-[22px] font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>{openCount}</p>
        </div>
        <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2.5" data-testid="kpi-need-attention">
          <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Need Attention</p>
          <p className="text-[22px] font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>{attentionCount}</p>
        </div>
        <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2.5">
          <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Settled</p>
          <p className="text-[22px] font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>{rows.filter(r => r.status === 'Settled').length}</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="pt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="h-4 w-4 text-slate-400 absolute left-2 top-2.5" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} className="pl-8" placeholder="Search by claim #, client, policy, insurer ref" data-testid="claims-search" />
            </div>
            <div className="w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="claims-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="attention">Needs Attention</SelectItem>
                  {CLAIM_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="claims-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {CLAIM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(statusFilter !== 'all' || typeFilter !== 'all' || query) && (
              <Button variant="ghost" size="sm" onClick={() => { setStatusFilter('all'); setTypeFilter('all'); setQuery(''); }} data-testid="claims-clear-filters">Clear</Button>
            )}
          </div>

          {filteredRows.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="claims-empty">
              <FileWarning className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No claims match this filter.</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTh sortKey="claim_number" state={table}>Claim #</SortableTh>
                    <SortableTh sortKey="client_name" state={table}>Client</SortableTh>
                    <SortableTh sortKey="policy_number" state={table}>Policy</SortableTh>
                    <SortableTh sortKey="claim_type" state={table}>Type</SortableTh>
                    <SortableTh sortKey="status" state={table}>Status</SortableTh>
                    <SortableTh sortKey="claimed_amount" state={table}>Claimed Amount</SortableTh>
                    <SortableTh sortKey="date_of_loss" state={table}>Date of Loss</SortableTh>
                    <SortableTh sortKey="next_followup_date" state={table}>Next Follow-up</SortableTh>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {table.pageRows.map((c) => {
                    const today = new Date().toISOString().slice(0, 10);
                    const overdue = OPEN_STATUSES.has(c.status) && c.next_followup_date && c.next_followup_date <= today;
                    return (
                      <TableRow key={c.claim_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/claims/${c.claim_id}`)} data-testid={`claims-row-${c.claim_id}`}>
                        <TableCell className="font-medium text-blue-800">{c.claim_number}</TableCell>
                        <TableCell>{c.client_name}</TableCell>
                        <TableCell className="text-xs text-slate-600">{c.policy_number || '—'}</TableCell>
                        <TableCell className="text-xs">{c.claim_type}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${statusTone(c.status)}`}>{c.status}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{c.claimed_amount ? formatCurrency(c.claimed_amount) : '—'}</TableCell>
                        <TableCell className="text-xs">{c.date_of_loss ? formatDate(c.date_of_loss) : '—'}</TableCell>
                        <TableCell className="text-xs">
                          {c.next_followup_date ? (
                            <span className={overdue ? 'text-red-700 font-semibold inline-flex items-center gap-1' : 'text-slate-700'}>
                              {overdue && <AlertTriangle className="h-3.5 w-3.5" />}
                              {formatDate(c.next_followup_date)}
                            </span>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" onClick={() => { setEditing(c); setDialogOpen(true); }} data-testid={`claims-edit-${c.claim_id}`}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleting(c)} data-testid={`claims-delete-${c.claim_id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <PaginationBar state={table} testId="claims-pagination" />
            </>
          )}
        </CardContent>
      </Card>

      <AddClaimDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSaved={() => load()}
        editing={editing}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <AlertDialogContent data-testid="claims-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this claim permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              Claim {deleting?.claim_number} for {deleting?.client_name} will be removed along with its notes and attached documents. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="claims-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete} data-testid="claims-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Claims;
